import type {
  HTMLAttributes,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  RefObject,
} from 'react';
import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { IconButton } from '@onyx/ui/components/colleague/icon-button';
import type { ListItemSelectableProps } from '@onyx/ui/components/colleague/list-item-selectable';
import { ListItemSelectable } from '@onyx/ui/components/colleague/list-item-selectable';

import styles from './menu.module.css';
import { useMenuPortalPosition } from './use-menu-portal-position/use-menu-portal-position';

export type MenuItem = ListItemSelectableProps & {
  id: string;
};

type ContainerHtmlAttributes = Pick<
  HTMLAttributes<HTMLDivElement>,
  | 'id'
  | 'role'
  | 'tabIndex'
  | 'aria-label'
  | 'aria-labelledby'
  | 'aria-describedby'
>;

export type MenuProps = ContainerHtmlAttributes & {
  items: MenuItem[];
  onSelect?: (item: MenuItem) => void;
  selectedId?: string;
  selectedIds?: string[];
  shouldCloseOnSelect?: boolean;
  trigger?: ReactElement;
  triggerAriaLabel?: string;
  isFullWidth?: boolean;
  isMultiSelect?: boolean;
  shouldMatchTriggerWidth?: boolean;
};

export const Menu = ({
  id,
  items,
  onSelect,
  selectedId,
  selectedIds,
  shouldCloseOnSelect = true,
  trigger,
  triggerAriaLabel = 'Open menu',
  role,
  tabIndex,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby,
  isFullWidth = false,
  isMultiSelect = false,
  shouldMatchTriggerWidth = false,
}: MenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSelectedId, setInternalSelectedId] = useState<
    string | undefined
  >(selectedId);
  const [internalSelectedItems, setInternalSelectedItems] = useState<string[]>(
    selectedIds ?? (selectedId ? [selectedId] : [])
  );

  useEffect(() => {
    setInternalSelectedId(selectedId);
  }, [selectedId]);

  useEffect(() => {
    if (selectedIds) {
      setInternalSelectedItems(selectedIds);
    }
  }, [selectedIds]);

  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerControlRef = useRef<HTMLElement | null>(null);
  const generatedId = useId();
  const menuId = `menu-${generatedId}`;
  const { portalContainer, portalStyle } = useMenuPortalPosition(
    isOpen,
    containerRef as RefObject<HTMLElement>,
    menuRef as RefObject<HTMLElement>,
    isFullWidth,
    shouldMatchTriggerWidth
  );

  const fallbackTrigger = useMemo(
    () => <IconButton icon="MoreVert" aria-label={triggerAriaLabel} />,
    [triggerAriaLabel]
  );

  const triggerElement = trigger ?? fallbackTrigger;

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: Event) => {
      const targetNode = event.target as Node;

      if (
        containerRef.current?.contains(targetNode) ||
        menuRef.current?.contains(targetNode)
      ) {
        return;
      }

      closeMenu();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        triggerControlRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeMenu, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return;
    }

    const firstFocusable = menuRef.current?.querySelector<HTMLButtonElement>(
      'button:not([disabled])'
    );

    firstFocusable?.focus();
  }, [isOpen]);

  const composedTrigger = useMemo(() => {
    const triggerProps = triggerElement.props ?? {};

    const handleTriggerClick = (event: ReactMouseEvent<HTMLElement>) => {
      triggerControlRef.current = event.currentTarget;
      triggerProps.onClick?.(event);

      if (event.defaultPrevented) {
        return;
      }

      handleToggle();
    };

    return cloneElement(triggerElement, {
      ...(!('aria-label' in triggerProps) && triggerAriaLabel
        ? { 'aria-label': triggerAriaLabel }
        : {}),
      'aria-haspopup': 'menu',
      'aria-expanded': isOpen,
      'aria-controls': menuId,
      onClick: handleTriggerClick,
    });
  }, [handleToggle, isOpen, menuId, triggerAriaLabel, triggerElement]);

  const handleItemSelect = useCallback(
    (item: MenuItem) => {
      onSelect?.(item);

      if (isMultiSelect) {
        setInternalSelectedItems((prev) => {
          if (prev.includes(item.id)) {
            return internalSelectedItems.filter((id) => id !== item.id);
          }

          return [...internalSelectedItems, item.id];
        });

        return;
      }

      if (selectedId === undefined) {
        setInternalSelectedId(item.id);
      }

      if (shouldCloseOnSelect) {
        closeMenu();
      }
    },
    [
      closeMenu,
      onSelect,
      selectedId,
      shouldCloseOnSelect,
      isMultiSelect,
      internalSelectedItems,
    ]
  );

  const resolvedSelectedId = selectedId ?? internalSelectedId;
  const resolvedSelectedItems = useCallback(
    (itemId: string) => internalSelectedItems.includes(itemId),
    [internalSelectedItems]
  );

  const renderedMenu = useMemo(() => {
    if (!isOpen || !portalContainer) {
      return null;
    }

    return createPortal(
      <div
        ref={menuRef}
        id={menuId}
        role="menu"
        className={
          styles.menu +
          (isFullWidth ? ` ${styles.fullWidth}` : '') +
          (shouldMatchTriggerWidth ? ` ${styles.matchTriggerWidth}` : '')
        }
        style={portalStyle ?? undefined}
        aria-orientation="vertical"
      >
        <div className={styles.menuList}>
          {items.map((item) => {
            const {
              id: itemId,
              isDisabled = false,
              isSelected,
              ...listItemProps
            } = item;

            const isResolvedSelected = isMultiSelect
              ? resolvedSelectedItems(itemId)
              : (isSelected ?? resolvedSelectedId === itemId);

            const onItemSelect = isDisabled
              ? undefined
              : () => handleItemSelect(item);

            return (
              <div key={itemId} className={styles.menuItem} role="none">
                <ListItemSelectable
                  {...listItemProps}
                  isSelected={isResolvedSelected}
                  isDisabled={isDisabled}
                  onSelect={onItemSelect}
                  variant={isMultiSelect ? 'checkbox' : listItemProps.variant}
                />
              </div>
            );
          })}
        </div>
      </div>,
      portalContainer
    );
  }, [
    handleItemSelect,
    isOpen,
    items,
    menuId,
    portalContainer,
    portalStyle,
    resolvedSelectedId,
    isFullWidth,
    shouldMatchTriggerWidth,
    isMultiSelect,
    resolvedSelectedItems,
  ]);

  return (
    <div
      ref={containerRef}
      id={id}
      role={role}
      tabIndex={tabIndex}
      className={styles.container}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
    >
      {composedTrigger}

      {renderedMenu}
    </div>
  );
};
